/**
 * Check if the logged_in cookie exists.
 * This cookie is set by the backend alongside auth tokens and has the same
 * expiration as the refresh token. Unlike httpOnly auth cookies, this one
 * is readable by JavaScript to optimize frontend behavior.
 */
export const hasAuthCookie = () => document.cookie.includes('logged_in=');

/**
 * Get a cookie value by name.
 */
export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) {
    try {
      // Handle URL-encoded values like %22dark%22 -> "dark" -> dark
      const decoded = decodeURIComponent(match[2]);
      // Remove surrounding quotes if present
      return decoded.replace(/^"|"$/g, '');
    } catch {
      return match[2];
    }
  }
  return null;
}

/**
 * Supported themes for the application.
 */
const SUPPORTED_THEMES = ['light', 'dark', 'system'] as const;
type SupportedTheme = (typeof SUPPORTED_THEMES)[number];

/**
 * Get theme from theme cookie.
 * Returns 'light', 'dark', or 'system'.
 */
export function getThemeFromCookie(): SupportedTheme {
  const theme = getCookie('polaris-theme');
  if (theme && SUPPORTED_THEMES.includes(theme as SupportedTheme)) {
    return theme as SupportedTheme;
  }
  return 'system';
}

/**
 * Set theme cookie.
 * Cookie expires in 1 year.
 */
export function setThemeCookie(theme: string): void {
  document.cookie = `polaris-theme=${theme}; path=/; max-age=31536000; SameSite=Lax`;
}

/**
 * Supported languages for the application.
 */
const SUPPORTED_LANGUAGES = ['en', 'es', 'sr'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Get language from language cookie.
 * Returns 'en', 'es', or 'sr'. Defaults to 'en'.
 */
export function getLanguageFromCookie(): SupportedLanguage {
  const lang = getCookie('polaris-lang');
  if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) {
    return lang as SupportedLanguage;
  }
  return 'en';
}

/**
 * Set language cookie.
 * Cookie expires in 1 year.
 */
export function setLanguageCookie(lang: string): void {
  document.cookie = `polaris-lang=${lang}; path=/; max-age=31536000; SameSite=Lax`;
}

/**
 * Get sidebar collapsed state from cookie.
 * Returns true if collapsed, false if expanded. Defaults to false (expanded).
 */
export function getSidebarCollapsedFromCookie(): boolean {
  const collapsed = getCookie('polaris-sidebar');
  return collapsed === 'true';
}

/**
 * Set sidebar collapsed state cookie.
 * Cookie expires in 1 year.
 */
export function setSidebarCollapsedCookie(collapsed: boolean): void {
  document.cookie = `polaris-sidebar=${collapsed}; path=/; max-age=31536000; SameSite=Lax`;
}
